import { chain } from "lodash";
import { createSelector } from "reselect";
import type { RootState } from "typesafe-actions";
import { LicenseType } from "@/enums/LicenseType.enum";
import { calculatePermissions } from "../../utils/sharing.helpers";
import type { ConfigState } from "../config/config.reducer";
import { showCreditLimitUi } from "../config/config.selector";
import type { AppState } from "../root.reducer";
import { user } from "../user/user.selector";
import { defaultRoles } from "./defaultRoles";
import type {
  Label,
  MyWorkspaceMetadata,
  RoleUser,
  WorkspaceRoles,
} from "./types";
import type { WorkspacesState } from "./workspaces.reducer";

export const workspace = (state: RootState): WorkspacesState =>
  state.context.workspaces;

export const selectWorkspacesSearchTerm = createSelector(
  [workspace],
  (workspacesState) => workspacesState.searchTerm,
);

export const selectWorkspacesSearchLabels = createSelector(
  [workspace],
  (workspacesState) => workspacesState.searchLabels,
);

export const selectWorkspacesSearchLabelsString = createSelector(
  [selectWorkspacesSearchLabels],
  (searchLabels) =>
    searchLabels
      .map(({ name }) => name)
      .filter(Boolean)
      .join(","),
);

export const selectDashboardIncludeHiddenWorkspaces = createSelector(
  [workspace],
  (workspacesState) => workspacesState.includeHiddenWorkspaces,
);

export const selectWorkspaces = createSelector(
  [workspace],
  (workspacesState) => workspacesState.workspaces,
);

export const selectDashboardDetails = createSelector(
  [workspace],
  (workspacesState) => workspacesState.dashboardDetails,
);

export const selectFullscreenWorkspaceId = createSelector(
  [selectDashboardDetails],
  (dashboardDetails) => dashboardDetails.fullscreenWorkspaceId,
);

export const selectDashboardWorkspaceDetails = createSelector(
  [selectDashboardDetails],
  (dashboardDetails) => dashboardDetails.workspaceDetails,
);

export const selectWorkspacesRoles = createSelector(
  [workspace],
  (workspacesState) => workspacesState.workspacesRoles,
);

export const selectIsUpdatingWorkspace = createSelector(
  [workspace],
  (workspaceState) => workspaceState.isUpdatingWorkspace,
);

export const selectWorkspacesEmailPreset = createSelector(
  [workspace],
  (workspaceState) => workspaceState.emailPreset,
);

export const selectWorkspacesShowOverlay = createSelector(
  [workspace],
  (workspaceState) => workspaceState.showOverlay,
);

export const selectDashboardActionOption = createSelector(
  [workspace],
  (workspaceState) => workspaceState.activeOption,
);

export const selectWorkspacesPagination = createSelector(
  [workspace],
  (workspacesState) => workspacesState.pagination,
);

export const selectStoredSearchParams = createSelector(
  [workspace],
  (workspaceState) => workspaceState.storedSearchParams,
);

export const selectWorkspacesWaitingToEditId = createSelector(
  [workspace],
  (workspacesState) => workspacesState.waitingToEditID,
);

export const selectWorkspacesWaitingToEditAllowTemplateEdit = createSelector(
  [workspace],
  (workspacesState) => !!workspacesState.waitingToEditAllowTemplateEdit,
);

export const selectWorkspacesLoadProgress = createSelector(
  [workspace],
  (workspacesState) => workspacesState.loadProgress ?? 0,
);

export const selectWorkspacesLoadState = createSelector(
  [workspace],
  (workspacesState) => workspacesState.loadState ?? 0,
);

const selectUserLicenseInfo = (state: RootState) =>
  state.context.user.licenseInfo;

export const selectHasAssignableProjects = (state: RootState) =>
  !!state.context.workspaces.assignableProjects?.length;

const tonnageUsage = (state: RootState) =>
  state.context.workspaces.tonnageUsage;

export const selectSelectedWorkspaceId = createSelector(
  [workspace],
  (workspaceState) => workspaceState.selectedWorkspaceID,
);

export const selectUserLicenseType = createSelector(
  [selectUserLicenseInfo],
  (license) => {
    if (!license) return LicenseType.Free;
    return license.type;
  },
);
export const getWaitingToLoadProjectID = (state: RootState) =>
  state.context.workspaces.waitingToLoadProjectID;

export const getWaitingToCreateFromTemplate = (state: RootState) =>
  state.context.workspaces.waitingToCreateFromTemplate;

export const selectIsUserLicensed = createSelector(
  [selectUserLicenseInfo],
  (license) => {
    if (!license) return false;
    return (
      [LicenseType.Plus, LicenseType.Enterprise].includes(license.type) &&
      !license.isExpired
    );
  },
);

export function getWorkspaceThumbnailUrl(
  workspaceId: string,
  config: ConfigState,
): string {
  return (
    config.serviceConfig.documentImages +
    "/api/v2/blob/" +
    workspaceId +
    "/thumbnail"
  );
}

export const selectWorkspaceById = createSelector(
  [selectWorkspaces, (_: RootState, workspaceId?: string) => workspaceId],
  (workspaces, workspaceId) =>
    workspaces.find((workspace) => workspace.workspaceId === workspaceId),
);

export const selectDashboardWorkspaceDetailsById = createSelector(
  [
    selectDashboardWorkspaceDetails,
    (_: RootState, workspaceId?: string) => workspaceId,
  ],
  (dashboardWorkspaceDetails, workspaceId) =>
    workspaceId ? dashboardWorkspaceDetails[workspaceId] : undefined,
);

export const createSelectDashboardWorkspaceDetailsById = () =>
  selectDashboardWorkspaceDetailsById;

export const isWorkspaceFullscreen = createSelector(
  [selectFullscreenWorkspaceId],
  (fullscreenWorkspaceId) => !!fullscreenWorkspaceId,
);

export const selectLiveWorkspaceIds = createSelector(
  [selectDashboardWorkspaceDetails],
  (dashboardWorkspaceDetails) =>
    chain(dashboardWorkspaceDetails)
      .pickBy((ws) => ws.isLive)
      .keys()
      .value(),
);

export const selectSelectedWorkspace = createSelector(
  [selectWorkspaces, selectSelectedWorkspaceId],
  (workspaces, selectedWorkspaceId) =>
    workspaces.find(
      (workspace) => workspace.workspaceId === selectedWorkspaceId,
    ),
);

const selectMyPermissionsByWorkspaceId = createSelector(
  [selectWorkspaceById],
  (workspace) => workspace?.myPermissions,
);

export const isWorkspaceLimited = createSelector(
  [selectMyPermissionsByWorkspaceId],
  (myPermissions) => !!(!myPermissions?.canGrow && myPermissions?.canWrite),
);

export const selectSelectedWorkspaceRolesBase = createSelector(
  [selectWorkspacesRoles, selectSelectedWorkspaceId],
  (workspacesRoles, selectedWorkspaceId): WorkspaceRoles => {
    const roles = !!selectedWorkspaceId && workspacesRoles[selectedWorkspaceId];

    return roles || defaultRoles();
  },
);

export const selectPublicPermissionLevel = createSelector(
  [selectSelectedWorkspaceRolesBase],
  (selectedWorkspaceRoles) =>
    calculatePermissions(selectedWorkspaceRoles.globalAccess),
);

export const selectSelectedWorkspaceRoles = createSelector(
  [selectSelectedWorkspaceRolesBase],
  (roles): WorkspaceRoles => {
    const processedEmails = new Set<string>();

    const processUserRoles = (arr: RoleUser[]) => {
      arr.forEach((v) => {
        v.email && processedEmails.add(v.email);
      });
      return arr;
    };

    const isNotProcessedRoleUser = (roleUser: RoleUser) =>
      roleUser.email && !processedEmails.has(roleUser.email);

    return {
      ...roles,
      administrators: processUserRoles(roles.administrators),
      writers: processUserRoles(roles.writers.filter(isNotProcessedRoleUser)),
      readers: roles.readers.filter(isNotProcessedRoleUser),
    };
  },
);

export const getAllowedPremiumModules = createSelector([user], (user) => {
  return user.licenseInfo?.allowedPremiumModules || [];
});

export const selectWorkspacesAllLabels = createSelector(
  [workspace],
  (workspacesState): Label[] => workspacesState.dashboardLabels,
);

export const selectIsFetchingDashboardLabels = createSelector(
  [workspace],
  (workspacesState) => workspacesState.isFetchingDashboardLabels,
);

export const selectHasModifiedThumbnail = (
  state: RootState,
  workspaceId?: string,
) =>
  !!workspaceId && state.context.workspaces.thumbnailsModified.has(workspaceId);

export const uploadedSnapshot = (state: RootState, workspaceId: string) => {
  return state.context.workspaces.thumbnailsModified.get(workspaceId);
};

export const selectEffectiveTonnageUsage = createSelector(
  [tonnageUsage, selectIsUserLicensed, showCreditLimitUi],
  (tonnage, isLicenseActive, showCreditLimitUi) => {
    if (isLicenseActive || !showCreditLimitUi) return 0;
    return tonnage;
  },
);

export const selectIsCreditLimitReached = createSelector(
  [selectEffectiveTonnageUsage],
  (tonnage) => tonnage >= 100,
);

/** @deprecated use selectIsCreditLimitReached and delete when all converted */
export const isCreditLimitReached = selectIsCreditLimitReached;

export const selectHiddenSectionIsVisible = (state: RootState) => {
  return (
    state.context.workspaces.includeHiddenWorkspaces &&
    !state.context.workspaces.selectedProject
  );
};

export const getDialogPosition = (state: RootState) => {
  return state.context.workspaces.dialogPosition;
};

export const isNotificationCenterOpen = (state: RootState) => {
  return state.context.workspaces.activeOption === "NOTIFICATION_CENTER";
};

export const selectTaskLocationData = (state: RootState) => {
  return state.context.workspaces.taskLocationsData;
};

type DashboardWorkspacesLists = {
  favorites: MyWorkspaceMetadata[];
  hidden: MyWorkspaceMetadata[];
  base: MyWorkspaceMetadata[];
};

export const selectMyWorkspacesMetadataLists = createSelector(
  [
    selectWorkspaces,
    selectDashboardWorkspaceDetails,
    (_: AppState, favoriteWorkspacesIds: string[] = []) =>
      new Set(favoriteWorkspacesIds),
  ],
  (workspaces, dashboardWorkspaceDetails, favoriteWorkspacesIdsSet) => {
    const dashboardWorkspacesLists = chain(workspaces)
      // Order by live workspaces ASC, then by lastAccess DESC
      .orderBy(
        [
          (w) => +!dashboardWorkspaceDetails[w.workspaceId]?.isLive, // truthy -> 0, falsey -> 1
          (w) => w.lastAccess ?? "", // This works because lastAccess contains a ISO date formatted string
        ],
        ["asc", "desc"],
      )
      // Group workspaces for UI sections
      .reduce<DashboardWorkspacesLists>(
        (acc, ws) => {
          switch (true) {
            case ws.isHidden:
              acc.hidden.push(ws);
              break;

            case favoriteWorkspacesIdsSet.has(ws.workspaceId):
              acc.favorites.push(ws);
              break;

            default:
              acc.base.push(ws);
              break;
          }
          return acc;
        },
        { favorites: [], hidden: [], base: [] },
      )
      .value();

    return dashboardWorkspacesLists;
  },
);
